import React, { useState, useEffect } from 'react';
import { rankInspectionRequests } from '../calculations/sortInspections';
import { getAvailableInspectorsForDate } from '../calculations/getInspectorAvailability';
import { assignInspectionsToInspectors } from '../calculations/assignInspections';
import { optimizeInspectorRoutes } from '../calculations/optimizeInspections';
import { calculateETAs } from '../calculations/calculateETAs';
import InspectorSelection from './components/InspectorSelection';
import InspectionSelection from './components/InspectionSelection';
import './SelectionDashboard.css';

function SelectionDashboard({ inspections, inspectors, selectedDate }) {
    const [sortedInspections, setSortedInspections] = useState([]);
    const [availableInspectors, setAvailableInspectors] = useState([]);
    const [selectedInspectors, setSelectedInspectors] = useState([]);
    const [selectedInspections, setSelectedInspections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [holidayInfo, setHolidayInfo] = useState(null);


    // console.log('selectedInspectors', selectedInspectors)
    // console.log('selectedInspections', selectedInspections)

    // Sort inspections and get available inspectors on component mount
    useEffect(() => {
        const processData = async () => {
            setIsLoading(true);
            setHolidayInfo(null);

            // Wrap the processing in a Promise to ensure loading state is shown
            await new Promise(resolve => {
                setTimeout(() => {
                    // Get today's date
                    // For testing, you can set a specific date
                    // const [year, month, day] = '2025-1-24'.split('-').map(Number);
                    // const today = new Date(year, month - 1, day, 12, 0, 0);
                    const today = selectedDate; // Use passed in selectedDate which is already in correct Date format
                    
                    // Check if it's a holiday first
                    const holiday = inspectors.holidayListData?.find(h => {
                        const holDate = new Date(h.START_DATE);
                        return holDate.getFullYear() === today.getFullYear() &&
                               holDate.getMonth() === today.getMonth() &&
                               holDate.getDate() === today.getDate();
                    });

                    if (holiday) {
                        setHolidayInfo(holiday);
                        setAvailableInspectors([]);
                    } else {
                        // Get available inspectors for today
                        const available = getAvailableInspectorsForDate(
                            inspectors.inspectorsData || [],
                            inspectors.calendarData || [],
                            inspectors.holidayListData || [],
                            today
                        );

                        const availableWithDistricts = addDistrictCodesToInspectors(available, inspectors.districtData);

                        // setAvailableInspectors(availableWithDistricts);
                        setAvailableInspectors(addDistrictCodesToInspectors(inspectors.inspectorsData, inspectors.districtData)); // testing w/o checking calendars/holidays
                    }

                    // Process inspections
                    if (inspections.inspectionsData) {
                        const ranked = rankInspectionRequests(inspections.inspectionsData, {
                            locationTieBreak: false
                        });

                        const rankedWithDuration = addImportedDurationToInspections(ranked, inspections.inspectionDurationData);

                        setSortedInspections(rankedWithDuration);
                    }

                    resolve();
                }, 0);
            });

            setIsLoading(false);
        };

        processData();
    }, [selectedDate, inspections.inspectionsData, inspectors.inspectorsData, inspectors.calendarData, inspectors.holidayListData]);


    // Helper function to add district codes to inspectors
    function addDistrictCodesToInspectors(inspectors, districtData) {
        if (!districtData) return inspectors;

        return inspectors.map(inspector => {
            // Find matching district data
            const inspectorDistrict = districtData.find(
                d => d.INSPECTOR_ID === inspector.INSPECTOR_ID
            );

            // If found, split the district string into an array, handling spaces
            const districtCodes = inspectorDistrict?.District
                ? inspectorDistrict.District.split('|')
                    .map(code => code.trim()) // Remove any spaces
                    .filter(code => code.length > 0) // Remove empty strings
                : [];

            // Return inspector with added districtCodes
            return {
                ...inspector,
                districtCodes
            };
        });
    }

    function addImportedDurationToInspections(inspections, durationData) {
        return inspections.map(inspection => {
            // Extract category code before the "-", accounting for spaces
            const categoryCode = inspection.ComplaintCategory.split('-')[0].trim();
            
            // Find matching duration data
            const matchedDuration = durationData?.find(
                d => d['Complaint Category'] === categoryCode
            );

            // Convert duration string to integer, fallback to existing DURATION if no match
            const duration = matchedDuration 
                ? parseInt(matchedDuration['Inspection Duration'], 10)
                : parseInt(inspection.DURATION, 10);

            // Add importedDuration as a number (not a string)
            return {
                ...inspection,
                importedDuration: isNaN(duration) ? 1800 : duration // default to 30 minutes (1800 seconds) if parsing fails
            };
        });
    }

    
    // Handle inspector selection
    const handleInspectorSelect = (inspectorId) => {
        if (holidayInfo) return; // Prevent selection on holidays
        setSelectedInspectors(prev => {
            if (prev.includes(inspectorId)) {
                return prev.filter(id => id !== inspectorId);
            }
            return [...prev, inspectorId];
        });
    };

    // Handle inspection selection
    const handleInspectionSelect = (inspectionId) => {
        if (holidayInfo) return; // Prevent selection on holidays
        setSelectedInspections(prev => {
            if (prev.includes(inspectionId)) {
                return prev.filter(id => id !== inspectionId);
            }
            return [...prev, inspectionId];
        });
    };

    async function goRoutes() {
        // Get full details of selected inspectors
        const selectedInspectorsDetails = availableInspectors.filter(inspector => 
            selectedInspectors.includes(inspector.INSPECTOR_ID)
        );

        // Get full details of selected inspections NOT CURRENTLY RELEVANT
        const selectedInspectionsDetails = sortedInspections.filter(inspection => 
            selectedInspections.includes(inspection.INSPECTION_ID)
        );

        // Split inspections into scheduled and unscheduled
        const prescheduledInspections = sortedInspections.filter(inspection =>
            inspection.Status === "Scheduled"
        );
        const unscheduledInspections = sortedInspections.filter(inspection => 
            inspection.Status !== "Scheduled"
        );

        // Assign inspections to inspectors
        const assignments = assignInspectionsToInspectors(selectedInspectorsDetails, unscheduledInspections);

        console.log('Selected Inspectors Details:', selectedInspectorsDetails);
        console.log('Assignment Results:', assignments);

        // for logging purposes
        let rankArray = [];
        for (let a = 0; a < assignments.length; a++) {
            let currInspections = assignments[a].assignedInspections;
            for (let b = 0; b < currInspections.length; b++) {
                rankArray.push(currInspections[b].rank);
            }
        }
        rankArray.sort((a, b) => a - b); // Sort numerically in ascending order
        console.log('Rank Array:', rankArray);

        // optimize routes
        const optimizedRoutes = await optimizeInspectorRoutes(assignments);
        // console.log('Optimized Routes:', optimizedRoutes);

        // calculate etas
        const optimizedRoutesWithETAs = calculateETAs(optimizedRoutes, selectedDate);
        console.log('Optimized Routes with ETAs:', optimizedRoutesWithETAs);

        
        // console.log('Prescheduled Inspections:', prescheduledInspections);

        
        // Download routes
        downloadRoutes(optimizedRoutesWithETAs);
    }
    
    /**
     * Creates a text file with inspection information in the format:
     * Record_ID|INSPECTION_ID|INSPECTOR_ID|AppointmentStart|AppointmentFinish
     * 
     * @param {Array} optimizedRoutesWithETAs - Array of optimized routes with ETAs
     */
    function downloadRoutes(optimizedRoutesWithETAs) {
        if (!optimizedRoutesWithETAs || optimizedRoutesWithETAs.length === 0) {
            console.warn('No routes to download');
            return;
        }
        
        // Create the content for the text file
        let fileContent = '';
        
        // Process each route
        optimizedRoutesWithETAs.forEach(route => {
            const inspectorId = route.assignedInspector.INSPECTOR_ID;
            
            // Process each inspection in the route
            route.assignedInspections.forEach(inspection => {
                // Format: Record_ID|INSPECTION_ID|INSPECTOR_ID|AppointmentStart|AppointmentFinish
                const row = `${inspection.Record_ID}|${inspection.INSPECTION_ID}|${inspectorId}|${inspection.AppointmentStart}|${inspection.AppointmentFinish}`;
                fileContent += row + '\n';
            });
        });
        
        // Create a Blob with the file content
        const blob = new Blob([fileContent], { type: 'text/plain' });
        
        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);
        
        // Create a link element
        const link = document.createElement('a');
        link.href = url;
        
        // Set the filename with the current date
        const date = selectedDate.toISOString().split('T')[0];
        link.download = `routes_${date}.txt`;
        
        // Append the link to the document
        document.body.appendChild(link);
        
        // Click the link to download the file
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('Routes downloaded successfully');
    }

    if (isLoading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-white">
                <div className="text-center">
                    <div className="spinner-inspections">
                        <div className="spinner-outer-circle"></div>
                        <div className="spinner-inner-circle"></div>
                    </div>
                    <h3 className="mt-8 text-xl font-medium text-gray-900">Processing Data</h3>
                    <p className="mt-2 text-sm text-gray-500">
                        Please wait while we sort the inspection data and retrieve available inspectors...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 pt-20 pb-5">
            <div className="h-full flex px-8">
                {/* Left panel - Inspector Selection */}
                <div className="w-1/3 h-full pr-4">
                    {holidayInfo ? (
                        <div>
                            <InspectorSelection
                                availableInspectors={availableInspectors}
                                inspectorsData={inspectors.inspectorsData}
                                selectedInspectors={selectedInspectors}
                                onInspectorSelect={handleInspectorSelect}
                            />
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mt-4">
                                <div className="flex items-center">
                                    <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="text-lg font-medium text-red-800">Holiday Notice</h3>
                                </div>
                                <p className="mt-2 text-sm text-red-700">
                                    {holidayInfo.HOLIDAY_NAME} - No inspectors are available on this holiday.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <InspectorSelection
                            availableInspectors={availableInspectors}
                            inspectorsData={inspectors.inspectorsData}
                            selectedInspectors={selectedInspectors}
                            onInspectorSelect={handleInspectorSelect}
                        />
                    )}
                </div>

                {/* Right panel - Inspection Selection */}
                <div className="w-2/3 h-full pl-4">
                    <InspectionSelection
                        sortedInspections={sortedInspections}
                        selectedInspections={selectedInspections}
                        onInspectionSelect={handleInspectionSelect}
                        goRoutes={goRoutes}
                    />
                </div>
            </div>
        </div>
    );
}

export default SelectionDashboard;